/**
 * Security Module
 *
 * Provides comprehensive security utilities for the application:
 * - Rate limiting to prevent brute force attacks
 * - Input sanitization to prevent XSS attacks
 * - CSRF token generation and validation
 * - Secure token storage
 * - Password strength validation
 */

// =============================================================================
// RATE LIMITING
// =============================================================================

interface RateLimitEntry {
  count: number;
  firstAttempt: number;
  blockedUntil: number | null;
}

const rateLimitStore = new Map<string, RateLimitEntry>();

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs: number;
}

const LOGIN_RATE_LIMIT: RateLimitConfig = {
  maxAttempts: 5,
  windowMs: 5 * 60 * 1000,
  blockDurationMs: 15 * 60 * 1000,
};

export function checkLoginRateLimit(identifier: string): {
  isLimited: boolean;
  remainingAttempts: number;
  blockedUntil: number | null;
  retryAfterSeconds: number | null;
} {
  const key = `login:${identifier}`;
  const now = Date.now();
  const entry = rateLimitStore.get(key);

  if (entry?.blockedUntil && now < entry.blockedUntil) {
    return {
      isLimited: true,
      remainingAttempts: 0,
      blockedUntil: entry.blockedUntil,
      retryAfterSeconds: Math.ceil((entry.blockedUntil - now) / 1000),
    };
  }

  if (entry && now - entry.firstAttempt > LOGIN_RATE_LIMIT.windowMs) {
    rateLimitStore.delete(key);
    return {
      isLimited: false,
      remainingAttempts: LOGIN_RATE_LIMIT.maxAttempts,
      blockedUntil: null,
      retryAfterSeconds: null,
    };
  }

  const currentCount = entry?.count || 0;
  return {
    isLimited: currentCount >= LOGIN_RATE_LIMIT.maxAttempts,
    remainingAttempts: Math.max(0, LOGIN_RATE_LIMIT.maxAttempts - currentCount),
    blockedUntil: entry?.blockedUntil || null,
    retryAfterSeconds: null,
  };
}

export function recordLoginAttempt(identifier: string): void {
  const key = `login:${identifier}`;
  const now = Date.now();
  const entry = rateLimitStore.get(key);

  if (!entry || now - entry.firstAttempt > LOGIN_RATE_LIMIT.windowMs) {
    rateLimitStore.set(key, { count: 1, firstAttempt: now, blockedUntil: null });
  } else {
    const newCount = entry.count + 1;
    rateLimitStore.set(key, {
      ...entry,
      count: newCount,
      blockedUntil: newCount >= LOGIN_RATE_LIMIT.maxAttempts
        ? now + LOGIN_RATE_LIMIT.blockDurationMs
        : null,
    });
  }
}

export function resetLoginRateLimit(identifier: string): void {
  rateLimitStore.delete(`login:${identifier}`);
}

// =============================================================================
// INPUT SANITIZATION
// =============================================================================

const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;',
  "'": '&#x27;', '/': '&#x2F;', '`': '&#x60;', '=': '&#x3D;',
};

export function escapeHtml(input: string): string {
  if (typeof input !== 'string') return '';
  return input.replace(/[&<>"'`=/]/g, (char) => HTML_ENTITIES[char] || char);
}

export function sanitizeInput(input: string): string {
  if (typeof input !== 'string') return '';
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript\s*:/gi, '')
    .trim();
}

export function sanitizeEmail(email: string): string {
  if (typeof email !== 'string') return '';
  return email.toLowerCase().trim().replace(/[<>'"`;(){}[\]\\]/g, '');
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// =============================================================================
// CSRF PROTECTION
// =============================================================================

export function generateSecureToken(length: number = 32): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
}

export function generateCsrfToken(): string {
  const token = generateSecureToken(32);
  sessionStorage.setItem('csrf_token', token);
  return token;
}

export function getCsrfToken(): string | null {
  return sessionStorage.getItem('csrf_token');
}

export function validateCsrfToken(token: string): boolean {
  const storedToken = getCsrfToken();
  if (!storedToken || !token || storedToken.length !== token.length) return false;
  let result = 0;
  for (let i = 0; i < storedToken.length; i++) {
    result |= storedToken.charCodeAt(i) ^ token.charCodeAt(i);
  }
  return result === 0;
}

// =============================================================================
// PASSWORD SECURITY
// =============================================================================

export interface PasswordStrength {
  score: number;
  label: string;
  suggestions: string[];
}

export function checkPasswordStrength(password: string): PasswordStrength {
  const suggestions: string[] = [];
  let score = 0;

  if (!password) return { score: 0, label: 'Molto debole', suggestions: ['Inserisci una password'] };

  if (password.length >= 8) score++;
  else suggestions.push('Usa almeno 8 caratteri');

  if (password.length >= 12) score++;
  if (/[a-z]/.test(password)) score += 0.5;
  else suggestions.push('Aggiungi lettere minuscole');
  if (/[A-Z]/.test(password)) score += 0.5;
  else suggestions.push('Aggiungi lettere maiuscole');
  if (/[0-9]/.test(password)) score += 0.5;
  else suggestions.push('Aggiungi numeri');
  if (/[^a-zA-Z0-9]/.test(password)) score += 0.5;
  else suggestions.push('Aggiungi caratteri speciali');

  const labels = ['Molto debole', 'Debole', 'Discreta', 'Forte', 'Molto forte'];
  const normalizedScore = Math.min(4, Math.max(0, Math.floor(score)));

  return { score: normalizedScore, label: labels[normalizedScore], suggestions };
}

// =============================================================================
// SESSION SECURITY
// =============================================================================

export function generateSessionFingerprint(): string {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
  ];
  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

export function storeSessionFingerprint(): void {
  sessionStorage.setItem('_session_fp', generateSessionFingerprint());
}

export function validateSessionFingerprint(): boolean {
  const stored = sessionStorage.getItem('_session_fp');
  if (!stored) return true;
  return stored === generateSessionFingerprint();
}
